/**
 * Wandering Profile API — backend calls for the V2 wandering device profile.
 *
 * Reads:
 *   getaccutechdevices             (device basic info by device_id)
 *   sp_dev_getresidentsbycampusid  (resident dropdown for assignment)
 *
 * Writes (all shared transTypes — backend unchanged from V1):
 *   device_edit_basic              (edit device info)
 *   device_unassign                (unassign + deactivate — via shared helper)
 *   device_delete                  (permanent delete — via shared helper)
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';
import { unassignDevices, deleteDevices } from '../../api/devicesApi.js';

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/** Fetch wandering device profile by campus + device ID. */
export function fetchWanderingProfile(campusId, deviceId) {
  return apiFetch({
    procedure: 'getaccutechdevices',
    reqType: 'stored',
    payload: { device_filter: deviceId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch all residents for the assignment dropdown. */
export function fetchResidentsForAssignment(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getresidentsbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE
// ---------------------------------------------------------------------------

/**
 * Edit wandering device basic info (description, serial, assignment).
 *
 * Mirrors editDeviceBasicInfo in device-profile/api/deviceProfileApi.js so
 * the backend transaction shape stays identical across device types.
 *
 * @param {Array}  payload    — [{ device_id, descriptions, manufacture_uuid, is_active, area_id, resident_id, device_use_id, modified_by, offsite, offsite_id }]
 * @param {Array}  oldPayload — [{ device_id, descriptions, manufacture_uuid, is_active, area_id, resident_id }]
 * @param {object} session    — { campusId, userId, clientId }
 */
export function editWanderingProfile(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'device_edit_basic',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Unassign and deactivate a wandering device. Wraps the shared
 * unassignDevices helper so the wandering profile honors the exact
 * same backend contract as the DevicesPage > Wandering tab.
 *
 * @param {object} device  — full device record (provides device_id, device_name, manufacture_id)
 * @param {object} session — { campusId, userId, clientId }
 */
export function unassignWanderingDevice(device, session) {
  return unassignDevices([device], session);
}

/**
 * Permanently delete a wandering device. Wraps the shared deleteDevices
 * helper for the same parity reason — single backend contract.
 *
 * @param {object} device  — full device record
 * @param {object} session — { campusId, userId, clientId }
 */
export function deleteWanderingDevice(device, session) {
  return deleteDevices([device], session);
}
